import { useMemo } from "react";

import { QueryResult, useQuery } from "@apollo/client";
import { ResultOf } from "@graphql-typed-document-node/core";

import { getFragmentData, graphql } from "@/__generated__";
import {
  QCurrentUserProviderQuery,
  QCurrentUserProviderQueryVariables,
  SourceControlProviderEnum,
} from "@/__generated__/graphql";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import createProvider from "@/providerFactories/createProvider";

const QCurrentUserProvider = graphql(`
  query QCurrentUserProvider($workspaceId: ID, $hasWorkspaceId: Boolean!) {
    currentUser {
      ...CurrentUserProvider_User
    }
  }
`);

const CurrentUserProvider_User = graphql(`
  fragment CurrentUserProvider_User on User {
    id
    databaseId
    confirmed
    fullName
    isEmployee
    featureFlags {
      key
      enabled
    }
    emailAddresses {
      nodes {
        id
        databaseId
        email
      }
    }
    primaryEmailAddress {
      id
      databaseId
      email
    }
    roles(workspaceId: $workspaceId) @include(if: $hasWorkspaceId) {
      nodes {
        id
        databaseId
        role
      }
    }
    settings {
      showNotifications
      defaultEnvironmentSortingKey
      defaultEnvironmentSortingOrder
    }
    identities {
      nodes {
        id
        identityProvider {
          id
          appClientName
          sourceControlProvider
          manageUrl
        }
        providerUsername
      }
    }
    ...UserAvatar_User
  }
`);

type GithubIdentity =
  | {
      id: string;
      providerUsername: string;
      manageUrl: string | null | undefined;
    }
  | undefined;

type CurrentUserProviderType = {
  currentUserResult?: QueryResult<QCurrentUserProviderQuery, QCurrentUserProviderQueryVariables>;
  currentUser?: ResultOf<typeof CurrentUserProvider_User>;
  githubIdentity: GithubIdentity;
};

const initialData: CurrentUserProviderType = {
  currentUserResult: undefined,
  currentUser: undefined,
  githubIdentity: undefined,
};

const useCurrentUserProviderHook = (): CurrentUserProviderType => {
  const { workspaceId } = useReadySearchParams();

  const currentUserResult = useQuery(QCurrentUserProvider, {
    variables: { workspaceId, hasWorkspaceId: !!workspaceId },
  });

  const currentUser =
    getFragmentData(CurrentUserProvider_User, currentUserResult?.data?.currentUser) ?? undefined;

  const githubIdentity = useMemo<GithubIdentity>(() => {
    const identity = currentUser?.identities?.nodes?.find(
      identity => identity?.identityProvider?.sourceControlProvider === SourceControlProviderEnum.Github
    );

    if (identity?.id && identity.providerUsername) {
      return {
        id: identity.id,
        providerUsername: identity.providerUsername,
        manageUrl: identity.identityProvider?.manageUrl,
      };
    }

    return undefined;
  }, [currentUser]);

  return {
    currentUserResult,
    currentUser,
    githubIdentity,
  };
};

export const [useCurrentUser, CurrentUserProvider, CurrentUserConsumer] = createProvider<
  CurrentUserProviderType,
  unknown
>(initialData, useCurrentUserProviderHook);
